import { Controller } from "stimulus";
import Canvas from "../../lib/canvas/canvas";
import ColorUtils from "../../lib/color_utils";
import DiplomaApiService from "../../lib/diploma_editor/diploma_api_service";
import { DIPLOMA_TEMPLATES } from "../../lib/diploma_editor/templates/index";

export default class extends Controller {
  // ===========================================================================
  // Static Declarations
  // ===========================================================================

  static outlets = ['flash-messages'];

  static targets = [
    // Canvas
    "canvas",

    // Background controls
    "bgColorPicker",
    "bgImageUpload",
    "bgImageUploadBtn",
    "bgImageInfo",
    "bgImageName",

    // Element creation
    "imageUpload",

    // Properties panels
    "textProperties",
    "lineProperties",
    "imageProperties",
    "deleteSection",

    // Text property controls
    "fontSize",
    "fontFamily",
    "textColor",
    "alignLeftBtn",
    "alignCenterBtn",
    "alignRightBtn",
    "textBoldBtn",
    "textItalicBtn",
    "textUnderlineBtn",

    // Line property controls
    "lineThickness",
    "lineColor",

    // History controls
    "undoBtn",
    "redoBtn",

    // Templates
    "templatesAccordion",

    // Form integration
    "dirtyField"
  ];

  static values = {
    uploadImagePath: String,
    diplomaId: Number,
    canvasData: Object,
    previewDisabledTooltip: String,
    backgroundImageSetText: String,
    imageUploadErrorMessage: String,
    bgImageUploadErrorMessage: String,
    templateBgImageUploadErrorMessage: String
  };

  // ===========================================================================
  // Lifecycle Methods
  // ===========================================================================

  connect() {
    this.canvas = new Canvas(this.canvasTarget);
    this.apiService = new DiplomaApiService();

    this._bindEventHandlers();
    this._attachEventListeners();
    this._loadInitialState();
  }

  disconnect() {
    this._detachEventListeners();
    this.canvas.destroy();
  }

  // ===========================================================================
  // Public Actions - Form Handling
  // ===========================================================================

  populateFormFields() {
    const form = this.element;

    this._populateCanvasField(form);
    this._populateBackgroundImageField(form);
    this._populateImageFields(form);
  }

  // ===========================================================================
  // Public Actions - Element Creation
  // ===========================================================================

  addText(event) {
    const textOptions = {
      'program_name': { text: '{program_name}', properties: { width: '600px' } },
      'learner_name': { text: '{learner_name}', properties: { width: '600px' } },
      'completion_date': { text: '{completion_date}', properties: { width: '400px' } },
      'program_description': { text: '{program_description}', properties: { width: '600px' } },
      'signature_1': { text: '{signature_1}', properties: { width: '400px' } },
      'signature_2': { text: '{signature_2}', properties: { width: '400px' } },
      'free_text': { text: 'Double-click to edit', properties: {} }
    };

    this.canvas.deselectAll();

    const textType = event.currentTarget.dataset.value;
    const option = textOptions[textType] || textOptions['free_text'];

    this.canvas.createTextElement(option.text, 100, 100, option.properties);
  }

  addLine() {
    this.canvas.deselectAll();
    this.canvas.createLineElement(100, 100, 200);
  }

  triggerImageUpload() {
    this.imageUploadTarget.click();
  }

  async imageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    const result = await this.apiService.uploadImage(file, this.uploadImagePathValue);

    if (result.success) {
      this.canvas.deselectAll();
      this.canvas.createImageElement(result.imageUrl, 100, 100, result.blobId);
      this.imageUploadTarget.value = '';
    } else {
      this.flashMessagesOutlet.displayErrorMessage(this.imageUploadErrorMessageValue);
    }
  }

  // ===========================================================================
  // Public Actions - Template Handling
  // ===========================================================================

  async loadTemplate(event) {
    const templateId = event.currentTarget.dataset.templateId;
    const template = DIPLOMA_TEMPLATES.find(t => t.id === templateId);

    if (!template) {
      console.error('Template not found:', templateId);
      return;
    }

    this.canvas.clearElements();

    const templateData = { ...template.data };
    const backgroundImagePath = templateData.backgroundImage?.url;
    delete templateData.backgroundImage;

    this.canvas.loadState(templateData);
    this._syncCanvasControls();

    if (backgroundImagePath) {
      const result = await this.apiService.uploadAssetImage(
        backgroundImagePath,
        this.uploadImagePathValue
      );

      if (result.success) {
        this.canvas.setBackgroundImage(result.imageUrl, result.blobId);
      } else {
        this.flashMessagesOutlet.displayErrorMessage(this.templateBgImageUploadErrorMessageValue);
      }
    }

    this._updateBackgroundImageInfo();
  }

  // ===========================================================================
  // Public Actions - Background Handling
  // ===========================================================================

  bgColorChange(e) {
    const color = e.detail?.value || e.target.value;

    this.canvas.setBackgroundColor(color);

    if (color.toUpperCase() !== '#FFFFFF') {
      if (this.canvas.getBackgroundImage()) {
        this.canvas.setBackgroundImage(null);
        this._updateBackgroundImageInfo();
      }
      this._disableBgImageUpload();
    } else {
      this._enableBgImageUpload();
    }
  }

  removeBgColor() {
    this.canvas.setBackgroundColor('#FFFFFF');
    if (this.hasBgColorPickerTarget) {
      this.bgColorPickerTarget.value = '#FFFFFF';
      this.bgColorPickerTarget.dispatchEvent(new Event('input'));
    }
    this._enableBgImageUpload();
  }

  triggerBgImageUpload() {
    this.bgImageUploadTarget.click();
  }

  async bgImageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    const result = await this.apiService.uploadImage(file, this.uploadImagePathValue);

    if (result.success) {
      this.canvas.setBackgroundImage(this._encodeImageUrlForCss(result.imageUrl), result.blobId);
      this.bgImageUploadTarget.value = '';
      this._updateBackgroundImageInfo(file.name);
      this._disableBgColorPicker({ resetColor: false });
    } else {
      this.flashMessagesOutlet.displayErrorMessage(this.bgImageUploadErrorMessageValue);
    }
  }

  _encodeImageUrlForCss(imageUrl) {
    return encodeURIComponent(imageUrl)
      .replace(/%2F/g, '/')
      .replace(/\(/g, '%28')
      .replace(/\)/g, '%29')
      .replace(/'/g, '%27');
  }

  removeBgImage() {
    this.canvas.setBackgroundImage(null);
    this._updateBackgroundImageInfo();
    this._enableBgColorPicker();
  }

  // ===========================================================================
  // Public Actions - Selection Actions
  // ===========================================================================

  deleteSelected() {
    this.canvas.deleteSelected();
  }

  duplicateSelected() {
    this.canvas.duplicateSelected();
  }

  // ===========================================================================
  // Public Actions - History
  // ===========================================================================

  undo() {
    this.canvas.undo();
  }

  redo() {
    this.canvas.redo();
  }

  // ===========================================================================
  // Public Actions - Text Property Handlers
  // ===========================================================================

  fontFamilyChange(e) {
    // e.target is a div containing the selected value
    const fontFamily = e.target.textContent.replace('\n', '').trim();
    this.canvas.getSelectedInstance().setFontFamily(fontFamily);
  }

  textColorChange(e) {
    const color = e.detail?.value || e.target.value;

    this.canvas.getSelectedInstance().setColor(color);
  }

  textAlignChange(e) {
    const value = e.detail?.value;

    if (value) {
      this.canvas.getSelectedInstance().setTextAlign(value);
    }
  }

  textStyleChange(e) {
    const { value, selected } = e.detail || {};

    const selectedInstance = this.canvas.getSelectedInstance();

    if (value === 'bold') {
      selectedInstance.setBold(selected);
    } else if (value === 'italic') {
      selectedInstance.setItalic(selected);
    } else if (value === 'underline') {
      selectedInstance.setUnderline(selected);
    }
  }

  // ===========================================================================
  // Public Actions - Line Property Handlers
  // ===========================================================================

  lineColorChange(e) {
    const color = e.detail?.value || e.target.value;
    this.canvas.getSelectedInstance().setColor(color);
  }

  // ===========================================================================
  // Private - Initialization Helpers
  // ===========================================================================

  _bindEventHandlers() {
    this._boundSelectionChanged = this._onSelectionChanged.bind(this);
    this._boundSelectionCleared = this._onSelectionCleared.bind(this);
    this._boundHistoryStateChanged = this._onHistoryStateChanged.bind(this);
    this._boundStateLoadedFromHistory = this._syncCanvasControls.bind(this);
    this._boundFontSizeChange = this._onFontSizeChange.bind(this);
    this._boundLineThicknessChange = this._onLineThicknessChange.bind(this);
    this._boundPreventClick = this._preventClick.bind(this);
    this._boundPreventEnterSubmit = this._preventEnterSubmit.bind(this);
  }

  _attachEventListeners() {
    document.addEventListener('promote:canvas--selectionChanged', this._boundSelectionChanged);
    document.addEventListener('promote:canvas--selectionCleared', this._boundSelectionCleared);
    document.addEventListener('promote:canvas--historyStateChanged', this._boundHistoryStateChanged);
    document.addEventListener('promote:canvas--stateLoadedFromHistory', this._boundStateLoadedFromHistory);

    this.element.addEventListener('keydown', this._boundPreventEnterSubmit);

    if (this.hasFontSizeTarget) {
      this.fontSizeTarget.addEventListener('blur', this._boundFontSizeChange);
    }

    if (this.hasLineThicknessTarget) {
      this.lineThicknessTarget.addEventListener('blur', this._boundLineThicknessChange);
    }
  }

  _detachEventListeners() {
    document.removeEventListener('promote:canvas--selectionChanged', this._boundSelectionChanged);
    document.removeEventListener('promote:canvas--selectionCleared', this._boundSelectionCleared);
    document.removeEventListener('promote:canvas--historyStateChanged', this._boundHistoryStateChanged);
    document.removeEventListener('promote:canvas--stateLoadedFromHistory', this._boundStateLoadedFromHistory);

    this.element.removeEventListener('keydown', this._boundPreventEnterSubmit);

    if (this.hasFontSizeTarget) {
      this.fontSizeTarget.removeEventListener('blur', this._boundFontSizeChange);
    }

    if (this.hasLineThicknessTarget) {
      this.lineThicknessTarget.removeEventListener('blur', this._boundLineThicknessChange);
    }
  }

  _preventEnterSubmit(e) {
    if (e.key === "Enter" && e.target.tagName === "INPUT" && e.target.type !== "submit") {
      e.preventDefault();
      e.target.blur();
    }
  }

  _loadInitialState() {
    const initialData = (this.hasCanvasDataValue && this.canvasDataValue) ? this.canvasDataValue : {};
    this.canvas.loadState(initialData);

    // Defer sync to allow child controllers (color picker, etc.) to connect first
    requestAnimationFrame(() => {
      this._syncCanvasControls();
      this._enablePreview();
    });
  }

  // ===========================================================================
  // Private - Event Handlers
  // ===========================================================================

  _onSelectionChanged() {
    const elementInstance = this.canvas.getSelectedInstance();
    const hasMultipleSelected = this.canvas.hasMultipleSelected();

    if (hasMultipleSelected) {
      this._hideTextPropertiesPanel();
      this._hideLinePropertiesPanel();
      this._hideImagePropertiesPanel();
    } else if (elementInstance?.isTextElement()) {
      this._showTextPropertiesPanel();
      this._hideLinePropertiesPanel();
      this._hideImagePropertiesPanel();
      this._syncTextControlsFromElement(elementInstance);
    } else if (elementInstance?.isLineElement()) {
      this._hideTextPropertiesPanel();
      this._showLinePropertiesPanel();
      this._hideImagePropertiesPanel();
      this._syncLineControlsFromElement(elementInstance);
    } else if (elementInstance?.isImageElement()) {
      this._hideTextPropertiesPanel();
      this._hideLinePropertiesPanel();
      this._showImagePropertiesPanel();
    } else {
      this._hideTextPropertiesPanel();
      this._hideLinePropertiesPanel();
      this._hideImagePropertiesPanel();
    }

    this._showDeleteSection();
    this._collapseTemplatesAccordion();
  }

  _onSelectionCleared() {
    this._hideTextPropertiesPanel();
    this._hideLinePropertiesPanel();
    this._hideImagePropertiesPanel();
    this._hideDeleteSection();
  }

  _onHistoryStateChanged(event) {
    const { canUndo, canRedo } = event.detail;

    this.undoBtnTarget.disabled = !canUndo;
    this.redoBtnTarget.disabled = !canRedo;

    if (canUndo) {
      this._markFormAsDirty();
      this._disablePreview();
    } else {
      this._enablePreview();
    }
  }

  _onFontSizeChange(e) {
    const selectedInstance = this.canvas.getSelectedInstance();
    if (selectedInstance) {
      selectedInstance.setFontSize(e.target.value);
    }
  }

  _onLineThicknessChange(e) {
    const selectedInstance = this.canvas.getSelectedInstance();
    if (selectedInstance) {
      selectedInstance.setThickness(parseInt(e.target.value));
    }
  }

  // ===========================================================================
  // Private - Panel Visibility
  // ===========================================================================

  _showTextPropertiesPanel() {
    this.textPropertiesTarget.classList.remove('hidden');
  }

  _hideTextPropertiesPanel() {
    this.textPropertiesTarget.classList.add('hidden');
  }

  _showLinePropertiesPanel() {
    if (this.hasLinePropertiesTarget) {
      this.linePropertiesTarget.classList.remove('hidden');
    }
  }

  _hideLinePropertiesPanel() {
    if (this.hasLinePropertiesTarget) {
      this.linePropertiesTarget.classList.add('hidden');
    }
  }

  _showImagePropertiesPanel() {
    if (this.hasImagePropertiesTarget) {
      this.imagePropertiesTarget.classList.remove('hidden');
    }
  }

  _hideImagePropertiesPanel() {
    if (this.hasImagePropertiesTarget) {
      this.imagePropertiesTarget.classList.add('hidden');
    }
  }

  _showDeleteSection() {
    if (this.hasDeleteSectionTarget) {
      this.deleteSectionTarget.classList.remove('hidden');
    }
  }

  _hideDeleteSection() {
    if (this.hasDeleteSectionTarget) {
      this.deleteSectionTarget.classList.add('hidden');
    }
  }

  _collapseTemplatesAccordion() {
    if (this.hasTemplatesAccordionTarget) {
      const content = this.templatesAccordionTarget.querySelector('.accordion-content');
      if (content && !content.classList.contains('collapsed')) {
        this.templatesAccordionTarget.querySelector('[data-components--accordion-target="toggler"]').click();
      }
    }
  }

  // ===========================================================================
  // Private - Background Controls
  // ===========================================================================

  _syncBackgroundControlsState() {
    const hasBackgroundImage = !!this.canvas.getBackgroundImage();
    const bgColor = ColorUtils.rgbToHex(this.canvas.getBackgroundColor()).toUpperCase();
    const hasNonWhiteColor = bgColor !== '#FFFFFF';

    if (hasBackgroundImage) {
      this._disableBgColorPicker({ resetColor: false });
    } else if (hasNonWhiteColor) {
      this._disableBgImageUpload();
    }
  }

  _disableBgColorPicker({ resetColor = true } = {}) {
    if (this.hasBgColorPickerTarget) {
      if (resetColor) {
        this._setColorPickerValue(this.bgColorPickerTarget, '#FFFFFF');
        this.canvas.setBackgroundColor('#FFFFFF');
      }

      this.bgColorPickerTarget.closest('.admin-color-picker')?.classList.add('opacity-50', 'pointer-events-none');
    }
  }

  _enableBgColorPicker() {
    if (this.hasBgColorPickerTarget) {
      this.bgColorPickerTarget.closest('.admin-color-picker')?.classList.remove('opacity-50', 'pointer-events-none');
    }
  }

  _disableBgImageUpload() {
    if (this.hasBgImageUploadBtnTarget) {
      this.bgImageUploadBtnTarget.disabled = true;
    }
  }

  _enableBgImageUpload() {
    if (this.hasBgImageUploadBtnTarget) {
      this.bgImageUploadBtnTarget.disabled = false;
    }
  }

  _updateBackgroundImageInfo(filename = null) {
    const backgroundImage = this.canvas.getBackgroundImage();

    if (!this.hasBgImageInfoTarget) return;

    if (backgroundImage) {
      this.bgImageInfoTarget.classList.remove('hidden');

      if (this.hasBgImageNameTarget) {
        this.bgImageNameTarget.textContent = filename || this.backgroundImageSetTextValue;
      }
    } else {
      this.bgImageInfoTarget.classList.add('hidden');

      if (this.hasBgImageNameTarget) {
        this.bgImageNameTarget.textContent = '';
      }
    }
  }

  // ===========================================================================
  // Private - Sync Helpers
  // ===========================================================================

  _syncCanvasControls() {
    this._syncBackgroundColorControl();
    this._syncBackgroundControlsState();
    this._updateBackgroundImageInfo();
  }

  _syncBackgroundColorControl() {
    if (this.hasBgColorPickerTarget) {
      const bgColor = ColorUtils.rgbToHex(this.canvas.getBackgroundColor()).toUpperCase();
      this._setColorPickerValue(this.bgColorPickerTarget, bgColor);
    }
  }

  _syncTextControlsFromElement(elementInstance) {
    this._syncTextStyleControls(elementInstance);
    this._syncTextAlignmentControls(elementInstance);
  }

  _syncTextStyleControls(elementInstance) {
    this.fontSizeTarget.value = elementInstance.getFontSize();
    this._syncFontFamilyDropdown(elementInstance.getFontFamily());

    if (this.hasTextColorTarget) {
      const color = ColorUtils.rgbToHex(elementInstance.getColor());
      this._setColorPickerValue(this.textColorTarget, color);
    }

    this._syncSegmentedButton(this.textBoldBtnTarget, elementInstance.isBold());
    this._syncSegmentedButton(this.textItalicBtnTarget, elementInstance.isItalic());
    this._syncSegmentedButton(this.textUnderlineBtnTarget, elementInstance.isUnderline());
  }

  _syncTextAlignmentControls(elementInstance) {
    const alignment = elementInstance.getTextAlign();

    this._syncSegmentedButton(this.alignLeftBtnTarget, alignment === 'left');
    this._syncSegmentedButton(this.alignCenterBtnTarget, alignment === 'center');
    this._syncSegmentedButton(this.alignRightBtnTarget, alignment === 'right');
  }

  _syncLineControlsFromElement(elementInstance) {
    if (this.hasLineThicknessTarget) {
      this.lineThicknessTarget.value = elementInstance.getThickness();
    }

    if (this.hasLineColorTarget) {
      const color = ColorUtils.rgbToHex(elementInstance.getColor());
      this._setColorPickerValue(this.lineColorTarget, color);
    }
  }

  _syncFontFamilyDropdown(fontFamily) {
    const dropdown = this.fontFamilyTarget;

    const current = dropdown.querySelector('.js-selected');
    if (current) {
      current.classList.remove('js-selected');
      current.querySelector('.js-menu-option-check')?.classList.add('invisible');
    }

    const match = dropdown.querySelector(`[data-value="${fontFamily}"]`);
    if (match) {
      match.classList.add('js-selected');
      match.querySelector('.js-menu-option-check')?.classList.remove('invisible');
    }

    const label = dropdown.querySelector('[data-components--admin-dropdowns-dropdown-target="label"]');
    if (label) label.textContent = fontFamily;
  }

  _syncSegmentedButton(button, isSelected) {
    if (!button) return;

    button.dataset.selected = isSelected ? 'true' : 'false';
  }

  // ===========================================================================
  // Private - Form Field Population
  // ===========================================================================

  _populateCanvasField(form) {
    const canvasField = form.querySelector('input[name="pdf_template[canvas]"]');
    canvasField.value = JSON.stringify(this.canvas.serializeState());
  }

  _populateBackgroundImageField(form) {
    const backgroundImageKey = this.canvas.getBackgroundImageKey();
    if (backgroundImageKey) {
      const bgImageField = form.querySelector('input[name="pdf_template[background_image]"]');
      bgImageField.value = backgroundImageKey;
    }
  }

  _populateImageFields(form) {
    form.querySelectorAll('input[name="pdf_template[images][]"]').forEach(input => input.remove());

    const imageElements = this.canvas.getImageElementInstances();
    const uploadedImages = imageElements.map(el => el.getKey()).filter(key => key);

    uploadedImages.forEach(signedBlobId => {
      const hiddenInput = document.createElement('input');
      hiddenInput.name = "pdf_template[images][]";
      hiddenInput.type = "hidden";
      hiddenInput.value = signedBlobId;
      form.appendChild(hiddenInput);
    });
  }

  // ===========================================================================
  // Private - Form/Preview State
  // ===========================================================================

  _markFormAsDirty() {
    if (this.hasDirtyFieldTarget) {
      this.dirtyFieldTarget.value = Date.now();
      this.dirtyFieldTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  _disablePreview() {
    const previewBtn = document.querySelector('[data-diploma-preview-btn]');
    if (previewBtn) {
      previewBtn.classList.add('opacity-50', 'cursor-not-allowed');
      previewBtn.title = this.previewDisabledTooltipValue;
      previewBtn.addEventListener('click', this._boundPreventClick);
    }
  }

  _enablePreview() {
    const previewBtn = document.querySelector('[data-diploma-preview-btn]');
    if (previewBtn) {
      previewBtn.classList.remove('opacity-50', 'cursor-not-allowed');
      previewBtn.title = '';
      previewBtn.removeEventListener('click', this._boundPreventClick);
    }
  }

  _preventClick(e) {
    e.preventDefault();
  }

  // ===========================================================================
  // Private - UI Helpers
  // ===========================================================================

  _setColorPickerValue(target, color) {
    const event = new CustomEvent('promote:color-picker:set', {
      detail: { value: color }
    });

    target.dispatchEvent(event);
  }
}
